/**
 * Registers a custom media browser filter.
 * 
 * This function adds a new filter to the provided filters object. The filter
 * allows for filtering media items based on a search term and an additional
 * parameter. The filter can be used to narrow down media items by type and
 * optionally exclude certain MIME types.
 * 
 * @param {Object} filters - The filters object to which the custom filter will be added.
 */
export default function registerCustomMediaBrowserFilter(filters) {
    /**
     * Adds the 'customMediaBrowserFilter' to the filters object.
     * 
     * This function returns a filter function that processes an array of media
     * items based on the provided search term and additional parameter.
     * 
     * @param {Object} filters - The filters object to which the custom filter will be added.
     */
    filters.filter('customMediaBrowserFilter', function() {
        /**
         * Filters the media items based on the search term and additional parameter.
         * 
         * @param {Array} data - The array of media items to filter.
         * @param {string} search - The search term to filter by.
         * @param {string} additional - An additional parameter to further refine the filter.
         * @returns {Array} - The filtered array of media items.
         */
        return function(data, search, additional) {
            // If no data is provided, return an empty array.
            if (!data) {
                return [];
            }

            // If no search term is provided, return the original data.
            if (!search) {
                return data;
            } else {
                // Convert the search term to lowercase for case-insensitive comparison.
                var _s = search.toLowerCase();

                // Filter the data array based on the search term and additional parameter.
                return data.filter(function(media) {
                    // Ensure the additional parameter is defined, defaulting to 'all' if not.
                    var additionalParam = typeof additional !== 'undefined' ? additional : 'all',
                        result;

                    // Determine the result based on the additional parameter.
                    switch(additionalParam) {
                        case 'measurementobject':
                            // SMELL: Hardcoded MIME type check ('svg') may not be flexible or maintainable.
                            result = media.type.toLowerCase().indexOf(_s) > -1 && media.mimetype.toLowerCase().indexOf('svg') === -1;
                            break;
                        case 'all':
                            result = media.type.toLowerCase().indexOf(_s) > -1;
                            break;
                        default:
                            // SMELL: The default case is overly complex and may be hard to understand.
                            result = media.type.toLowerCase().indexOf(_s) > -1 || media.type.toLowerCase().indexOf(additionalParam.toLowerCase()) > -1;
                    }
                    return result;
                });
            }
        };
    });
}